export default function PaperworkSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-6">
            <span className="text-teal-700 text-sm font-semibold">Paperwork Made Simple</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            We Handle All the Paperwork
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            One of the biggest advantages of working with T-Rex Real Estate is our comprehensive paperwork handling service. We take care of every document, form, and legal requirement so you don't have to.
          </p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-12 mb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-file-list-3-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Probate Documents</h4>
              <p className="text-sm text-gray-600">All court filings and probate paperwork</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-shield-check-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Title Transfers</h4>
              <p className="text-sm text-gray-600">Complete title work and ownership transfer</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-government-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Tax Documents</h4>
              <p className="text-sm text-gray-600">Property tax and estate tax paperwork</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-file-text-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Closing Documents</h4>
              <p className="text-sm text-gray-600">All sale and transfer paperwork</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Understanding Texas Probate for Inherited Properties
            </h3>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              In Texas, when someone passes away and leaves real estate, the property typically must go through probate before it can be sold. Probate is the legal process of transferring ownership from the deceased person to their heirs. This process can be complex, time-consuming, and expensive if you don't have experienced guidance.
            </p>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              There are several types of probate in Texas, including independent administration, dependent administration, and muniment of title. The type of probate required depends on whether the deceased left a will, the value of the estate, and other factors. Each type has different requirements, timelines, and costs.
            </p>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Our team works with experienced probate attorneys who specialize in Texas estate law. We guide you through the entire process, explaining each step in plain language and handling all the paperwork on your behalf. Whether you're dealing with a simple estate or a complex probate situation, we have the expertise to navigate it successfully.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
              <div className="flex items-start space-x-4">
                <i className="ri-information-line text-2xl text-teal-600 flex-shrink-0 mt-1"></i>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Important Note</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Even if you're in the middle of probate, we can still make you an offer on the inherited property. We'll work with your probate attorney to ensure all legal requirements are met and the sale proceeds smoothly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/090f5cfc3b70209f2c2825af0d490405.jpg"
                alt="Inherited Property Paperwork"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="relative h-96 lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20attorney%20or%20legal%20expert%20reviewing%20inherited%20property%20documents%20confident%20expression%20modern%20law%20office%20professional%20photography%20natural%20lighting%20clean%20simple%20background&width=800&height=600&seq=inherited-paperwork-002&orientation=landscape"
                alt="Legal Expert Handling Paperwork"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">
                Documents We Handle for You
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Death certificates and probate court filings</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Letters testamentary or letters of administration</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Property title searches and title insurance</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Deed transfers and ownership documentation</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Lien releases and mortgage payoff statements</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Property tax documentation and clearances</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Closing statements and settlement documents</p>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <p className="text-gray-100">Distribution agreements for multiple heirs</p>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-700">
                <p className="text-teal-100 mb-6">
                  Our experienced team has handled hundreds of inherited property transactions in El Paso. We know exactly what documents are needed and how to process them efficiently.
                </p>
                <a
                  href="tel:+1-915-206-5814"
                  className="inline-flex items-center px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white text-lg font-bold rounded-lg transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill mr-2"></i>
                  Call (915) 206-5814
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}